/**
 * ReportLoadingSkeleton Component
 * Loading skeleton states for report components
 *
 * Story 1.6: Report Loading States & Error Handling
 */

import React from 'react';
import { clsx } from 'clsx';
import { Card } from '../common';

interface SkeletonProps {
  className?: string;
}

/**
 * Base skeleton pulse component
 */
const Skeleton: React.FC<SkeletonProps & { width?: string; height?: string }> = ({
  className,
  width = 'w-full',
  height = 'h-4',
}) => (
  <div
    className={clsx(
      'animate-pulse bg-stone-200 rounded',
      width,
      height,
      className
    )}
  />
);

/**
 * Summary card skeleton
 */
export const SummaryCardSkeleton: React.FC<SkeletonProps> = ({ className }) => (
  <Card className={clsx('p-6', className)}>
    <Skeleton height="h-3" width="w-24" className="mb-3" />
    <Skeleton height="h-8" width="w-20" className="mb-2" />
    <Skeleton height="h-3" width="w-16" />
  </Card>
);

/**
 * Multiple summary cards skeleton
 */
export const SummaryCardsSkeleton: React.FC<{ count?: number }> = ({ count = 4 }) => (
  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
    {Array.from({ length: count }).map((_, i) => (
      <SummaryCardSkeleton key={i} />
    ))}
  </div>
);

/**
 * Chart skeleton (bar/line chart)
 */
export const ChartSkeleton: React.FC<SkeletonProps & { height?: string }> = ({
  className,
  height = 'h-64',
}) => (
  <Card className={clsx('p-6', className)}>
    <Skeleton height="h-4" width="w-32" className="mb-4" />
    <div className={clsx('flex items-end gap-2', height)}>
      {Array.from({ length: 8 }).map((_, i) => (
        <div key={i} className="flex-1 flex flex-col justify-end">
          <Skeleton
            height={`h-${Math.floor(Math.random() * 40 + 20)}%`}
            className="min-h-8"
          />
        </div>
      ))}
    </div>
    <div className="flex justify-between mt-4">
      {Array.from({ length: 8 }).map((_, i) => (
        <Skeleton key={i} height="h-2" width="w-8" />
      ))}
    </div>
  </Card>
);

/**
 * Pie chart skeleton
 */
export const PieChartSkeleton: React.FC<SkeletonProps> = ({ className }) => (
  <Card className={clsx('p-6', className)}>
    <Skeleton height="h-4" width="w-32" className="mb-4" />
    <div className="flex items-center justify-center">
      <div className="w-48 h-48 rounded-full bg-stone-200 animate-pulse flex items-center justify-center">
        <div className="w-24 h-24 rounded-full bg-white" />
      </div>
    </div>
    <div className="flex justify-center gap-4 mt-4">
      {Array.from({ length: 4 }).map((_, i) => (
        <div key={i} className="flex items-center gap-2">
          <div className="w-3 h-3 rounded-full bg-stone-200 animate-pulse" />
          <Skeleton height="h-3" width="w-12" />
        </div>
      ))}
    </div>
  </Card>
);

/**
 * Table skeleton
 */
export const TableSkeleton: React.FC<SkeletonProps & { rows?: number; columns?: number }> = ({
  className,
  rows = 5,
  columns = 4,
}) => (
  <Card className={clsx('overflow-hidden', className)}>
    {/* Header */}
    <div className="bg-stone-50 px-6 py-4 border-b border-stone-200">
      <div className="flex gap-4">
        {Array.from({ length: columns }).map((_, i) => (
          <Skeleton key={i} height="h-4" width={`w-${20 + i * 4}`} />
        ))}
      </div>
    </div>
    {/* Rows */}
    {Array.from({ length: rows }).map((_, rowIndex) => (
      <div
        key={rowIndex}
        className="px-6 py-4 border-b border-stone-100 last:border-0"
      >
        <div className="flex gap-4">
          {Array.from({ length: columns }).map((_, colIndex) => (
            <Skeleton
              key={colIndex}
              height="h-4"
              width={colIndex === 0 ? 'w-32' : 'w-16'}
            />
          ))}
        </div>
      </div>
    ))}
  </Card>
);

/**
 * Leaderboard skeleton
 */
export const LeaderboardSkeleton: React.FC<SkeletonProps & { rows?: number }> = ({
  className,
  rows = 5,
}) => (
  <Card className={clsx('p-6', className)}>
    <Skeleton height="h-5" width="w-40" className="mb-4" />
    <div className="space-y-3">
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i} className="flex items-center gap-3">
          <Skeleton height="h-8" width="w-8" className="rounded-full" />
          <div className="flex-1">
            <Skeleton height="h-4" width="w-32" className="mb-1" />
            <Skeleton height="h-2" width="w-full" />
          </div>
          <Skeleton height="h-4" width="w-12" />
        </div>
      ))}
    </div>
  </Card>
);

/**
 * Insights skeleton
 */
export const InsightsSkeleton: React.FC<SkeletonProps> = ({ className }) => (
  <Card className={clsx('p-6', className)}>
    <Skeleton height="h-5" width="w-32" className="mb-4" />
    <div className="space-y-3">
      {Array.from({ length: 3 }).map((_, i) => (
        <div key={i} className="flex items-start gap-3 p-3 bg-stone-50 rounded-lg">
          <div className="w-8 h-8 rounded-full bg-stone-200 animate-pulse flex-shrink-0" />
          <div className="flex-1">
            <Skeleton height="h-4" width="w-full" className="mb-2" />
            <Skeleton height="h-3" width="w-3/4" />
          </div>
        </div>
      ))}
    </div>
  </Card>
);

/**
 * Full dashboard skeleton (Executive Dashboard)
 */
export const ExecutiveDashboardSkeleton: React.FC = () => (
  <div className="space-y-6">
    {/* Summary Cards */}
    <SummaryCardsSkeleton count={4} />

    {/* Charts Row */}
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <ChartSkeleton height="h-72" />
      <PieChartSkeleton />
    </div>

    {/* Bottom Row */}
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <LeaderboardSkeleton rows={5} />
      <InsightsSkeleton />
    </div>
  </div>
);

/**
 * Engagement report skeleton
 */
export const EngagementReportSkeleton: React.FC = () => (
  <div className="space-y-6">
    <SummaryCardsSkeleton count={3} />
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <ChartSkeleton height="h-64" />
      <ChartSkeleton height="h-64" />
    </div>
    <TableSkeleton rows={5} columns={5} />
  </div>
);

/**
 * OKR Health report skeleton
 */
export const OKRHealthReportSkeleton: React.FC = () => (
  <div className="space-y-6">
    <SummaryCardsSkeleton count={3} />
    <ChartSkeleton height="h-72" />
    <TableSkeleton rows={8} columns={6} />
  </div>
);

/**
 * User Adoption report skeleton
 */
export const UserAdoptionReportSkeleton: React.FC = () => (
  <div className="space-y-6">
    {/* Summary cards */}
    <SummaryCardsSkeleton count={4} />

    {/* Funnel and Time to OKR charts */}
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <Card className="p-6">
        <div className="animate-pulse">
          <div className="h-5 w-40 bg-stone-200 rounded mb-4" />
          <div className="space-y-4">
            {[100, 85, 70, 55, 40].map((width, i) => (
              <div key={i} className="flex items-center gap-4">
                <div className="h-10 bg-stone-200 rounded" style={{ width: `${width}%` }} />
                <div className="h-4 w-16 bg-stone-200 rounded" />
              </div>
            ))}
          </div>
        </div>
      </Card>
      <ChartSkeleton height="h-64" />
    </div>

    {/* Feature discovery and retention */}
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <Card className="p-6">
        <div className="animate-pulse">
          <div className="h-5 w-40 bg-stone-200 rounded mb-4" />
          <div className="space-y-3">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="flex items-center gap-4 p-3 bg-stone-50 rounded-lg">
                <div className="w-10 h-10 rounded-lg bg-stone-200" />
                <div className="flex-1">
                  <div className="h-4 w-24 bg-stone-200 rounded mb-2" />
                  <div className="h-2 w-full bg-stone-200 rounded" />
                </div>
                <div className="h-6 w-12 bg-stone-200 rounded" />
              </div>
            ))}
          </div>
        </div>
      </Card>
      <Card className="p-6">
        <div className="animate-pulse">
          <div className="h-5 w-40 bg-stone-200 rounded mb-4" />
          <div className="grid grid-cols-3 gap-4 mb-4">
            {Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="p-4 bg-stone-100 rounded-lg text-center">
                <div className="h-8 w-16 bg-stone-200 rounded mx-auto mb-2" />
                <div className="h-3 w-20 bg-stone-200 rounded mx-auto" />
              </div>
            ))}
          </div>
          <div className="space-y-2">
            {Array.from({ length: 2 }).map((_, i) => (
              <div key={i} className="h-12 w-full bg-stone-200 rounded-lg" />
            ))}
          </div>
        </div>
      </Card>
    </div>
  </div>
);

export default {
  Skeleton,
  SummaryCardSkeleton,
  SummaryCardsSkeleton,
  ChartSkeleton,
  PieChartSkeleton,
  TableSkeleton,
  LeaderboardSkeleton,
  InsightsSkeleton,
  ExecutiveDashboardSkeleton,
  EngagementReportSkeleton,
  OKRHealthReportSkeleton,
  UserAdoptionReportSkeleton,
};
